import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getSatellitePasses } from "../services/api";
import axios from "axios";
import { RootState } from "./store";
import { SatellitePass } from "../types"; // Import SatellitePass from types.ts

interface SatellitesState {
  passes: SatellitePass[];
  filteredPasses: SatellitePass[];
  currentFilter: string;
  searchQuery: string;
  loading: boolean;
  error: string | null;
  favorites: (string | number)[];
  favoritesLoading: boolean;
  favoritesError: string | null;
  lastUpdated: string | null;
}

const initialState: SatellitesState = {
  passes: [],
  filteredPasses: [],
  currentFilter: "all",
  searchQuery: "",
  loading: false,
  error: null,
  favorites: [],
  favoritesLoading: false,
  favoritesError: null,
  lastUpdated: null,
};

// Enhanced thunk with location param
export const fetchSatellitePasses = createAsyncThunk(
  "satellites/fetchSatellitePasses",
  async (
    params: {
      latitude?: number;
      longitude?: number;
      days?: number;
    } = {},
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      // Get user's location from profile if not specified
      let { latitude, longitude, days = 7 } = params;

      if (!latitude || !longitude) {
        const profile = state.profile.profile;
        latitude = profile?.location?.latitude || profile?.latitude;
        longitude = profile?.location?.longitude || profile?.longitude;
      }

      // Fall back to default location if still no coordinates
      if (!latitude || !longitude) {
        latitude = 37.7749; // San Francisco latitude
        longitude = -122.4194; // San Francisco longitude
      }

      const data = await getSatellitePasses(latitude, longitude, days);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch satellite passes",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

// Add/remove favorites
export const toggleFavoritePass = createAsyncThunk(
  "satellites/toggleFavoritePass",
  async (passId: string | number, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      // Check if already a favorite
      const isFavorite = state.satellites.favorites.includes(passId);

      const endpoint = `/api/favorites/${isFavorite ? "remove" : "add"}`;

      await axios.post(
        endpoint,
        { passId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return { passId, isFavorite: !isFavorite };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to update favorite",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

// Fetch user's favorite passes
export const fetchFavorites = createAsyncThunk(
  "satellites/fetchFavorites",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const response = await axios.get("/api/favorites", {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data.favorites;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch favorites",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

const satellitesSlice = createSlice({
  name: "satellites",
  initialState,
  reducers: {
    setFilter(state, action: PayloadAction<string>) {
      state.currentFilter = action.payload;
      state.filteredPasses = applyFilters(
        state.passes,
        action.payload,
        state.searchQuery,
        state.favorites,
      );
    },

    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
      state.filteredPasses = applyFilters(
        state.passes,
        state.currentFilter,
        action.payload,
        state.favorites,
      );
    },

    clearFilters(state) {
      state.currentFilter = "all";
      state.searchQuery = "";
      state.filteredPasses = state.passes.map((pass: SatellitePass) => ({
        ...pass,
        isFavorite: state.favorites.includes(pass.id ?? pass.name), // Use name as fallback
      }));
    },

    showOnlyFavorites(state, action: PayloadAction<boolean>) {
      state.filteredPasses = applyFilters(
        state.passes,
        state.currentFilter,
        state.searchQuery,
        state.favorites,
        action.payload,
      );
    },
  },
  extraReducers: (builder) => {
    // Handle fetchSatellitePasses
    builder
      .addCase(fetchSatellitePasses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSatellitePasses.fulfilled, (state, action) => {
        state.loading = false;

        // Add the isFavorite property to each pass
        state.passes = action.payload.map((pass: SatellitePass) => ({
          ...pass,
          predicted_viewing_score: pass.predicted_viewing_score ?? 0, // Default to 0 if undefined
          isFavorite: state.favorites.includes(pass.id ?? pass.name), // Use name as fallback
        }));

        // Apply any existing filters
        state.filteredPasses = applyFilters(
          state.passes,
          state.currentFilter,
          state.searchQuery,
          state.favorites,
        );

        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchSatellitePasses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle toggleFavoritePass
      .addCase(toggleFavoritePass.fulfilled, (state, action) => {
        const { passId, isFavorite } = action.payload;

        // Update favorites array
        if (isFavorite) {
          state.favorites.push(passId);
        } else {
          state.favorites = state.favorites.filter((id) => id !== passId);
        }

        // Update isFavorite property in passes
        state.passes = state.passes.map((pass: SatellitePass) => {
          if ((pass.id ?? pass.name) === passId) {
            return { ...pass, isFavorite };
          }
          return pass;
        });

        // Update filtered passes too
        state.filteredPasses = state.filteredPasses.map(
          (pass: SatellitePass) => {
            if ((pass.id ?? pass.name) === passId) {
              return { ...pass, isFavorite };
            }
            return pass;
          },
        );
      })

      // Handle fetchFavorites
      .addCase(fetchFavorites.pending, (state) => {
        state.favoritesLoading = true;
        state.favoritesError = null;
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.favoritesLoading = false;
        state.favorites = action.payload;

        // Update isFavorite property in passes
        state.passes = state.passes.map((pass: SatellitePass) => ({
          ...pass,
          isFavorite: state.favorites.includes(pass.id ?? pass.name),
        }));

        // Update filtered passes too
        state.filteredPasses = state.filteredPasses.map(
          (pass: SatellitePass) => ({
            ...pass,
            isFavorite: state.favorites.includes(pass.id ?? pass.name),
          }),
        );
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.favoritesLoading = false;
        state.favoritesError = action.payload as string;
      });
  },
});

// Helper function to apply filters to passes
function applyFilters(
  passes: SatellitePass[],
  filter: string,
  search: string,
  favorites: (string | number)[],
  favoritesOnly: boolean = false,
): SatellitePass[] {
  return passes.filter((pass: SatellitePass) => {
    // Apply type filter - use satellite_type instead of type
    const typeMatch =
      filter === "all"
        ? true
        : pass.satellite_type?.toLowerCase() === filter.toLowerCase() || false;

    // Apply search filter
    const searchMatch = !search
      ? true
      : pass.name.toLowerCase().includes(search.toLowerCase());

    // Apply favorites filter if needed
    const favoriteMatch = favoritesOnly
      ? favorites.includes(pass.id ?? pass.name)
      : true;

    return typeMatch && searchMatch && favoriteMatch;
  });
}

export const { setFilter, setSearchQuery, clearFilters, showOnlyFavorites } =
  satellitesSlice.actions;

// Selectors
export const selectAllPasses = (state: RootState) => state.satellites.passes;
export const selectFilteredPasses = (state: RootState) =>
  state.satellites.filteredPasses;
export const selectSatellitesLoading = (state: RootState) =>
  state.satellites.loading;
export const selectSatellitesError = (state: RootState) =>
  state.satellites.error;
export const selectCurrentFilter = (state: RootState) =>
  state.satellites.currentFilter;
export const selectSearchQuery = (state: RootState) =>
  state.satellites.searchQuery;
export const selectFavorites = (state: RootState) => state.satellites.favorites;
export const selectFavoritePasses = (state: RootState) =>
  state.satellites.passes.filter((pass: SatellitePass) =>
    state.satellites.favorites.includes(pass.id ?? pass.name),
  );

export default satellitesSlice.reducer;
